import React from 'react';
import ManageTemplate, { ManageTemplateInstance } from '../../ui/ManageTemplate';
import { useParams } from 'react-router-dom';
import { useFormControl, useFormGroup, useManageTemplateForm } from '../../../utils/form.utils';
import { useQuery } from '@apollo/react-hooks';
import { ExtendedTheme, Loader, LargeUserCircle, TextField, Checkbox } from 'neon-design-system';
import { EDIT_USER, USER } from '../../../gql/users';
import { createStyles, makeStyles } from '@material-ui/core';
import FormTextField from '../../ui/form/FormTextField';
import { User } from '../../../shared/types';
import moment from 'moment';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { useUser } from '../../providers/UserProvider';
import { ResearchRole } from '../../../shared/global-enums';

const returnListUrl = baseUrls.publisher.users.list;

const useStyles = makeStyles((theme: ExtendedTheme) => createStyles({
	container: {
		display: 'flex',
		justifyContent: 'center',
		width: '100%',
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		paddingBottom: theme.spacing(6)
	},
	main: {
		flex: 1,
		flexDirection: 'column',
		maxWidth: 1200
	},
	userInfo: {
		display: 'flex',
		flex: 1,
		alignItems: 'center',
		backgroundColor: theme.themeColors.blueGrey[800],
		height: 80,
		marginBottom: theme.spacing(2)
	},
	helperCol: {
		display: 'flex',
		flexDirection: 'column',
		marginRight: theme.spacing(4),
		'&:last-child': {
			marginRight: theme.spacing(2)
		}
	},
	iconCol: {
		height: 44,
		width: 44,
		marginLeft: theme.spacing(2.25),
		marginRight: theme.spacing(4),
		fill: theme.themeColors.blueGrey[200]
	},
	helperColLabel: {
		...theme.typography.overline,
		fontWeight: 'bold',
		color: theme.themeColors.blueGrey[400],
		marginTop: 0,
		marginBottom: theme.spacing(0.25)
	},
	helperColValue: {
		...theme.typography.subtitle2
	},
	disabledTextField: {
		marginBottom: theme.spacing(2)
	},
	checkboxWrapper: {
		'& > label': {
			marginBottom: 0
		}
	}
}));

const UserEdit = () => {
	const classes = useStyles();
	const params = useParams<{ _id: string }>();
	const _id = window.decodeURIComponent(params._id);
	const { hasScope } = useUser();
	const isAdmin = [ResearchRole.Administrator].some(hasScope);

	const form = useFormGroup({
		position: useFormControl<string>(''),
		displayAvatar: useFormControl<boolean>(false),
		tableauAccess: useFormControl<boolean>(false)
	});

	const { loading, data } = useQuery<{ document: User }>(USER, {
		skip: !_id,
		fetchPolicy: 'network-only',
		variables: { id: _id },
		onCompleted: ({ document }) => {
			form.position.setInitialValue(document.position);
			form.displayAvatar.setInitialValue(document.displayAvatar);
			form.tableauAccess.setInitialValue(document.tableauAccess);
		}
	});

	const user: Partial<User> = data?.document ?? {};

	const { formMessage, formBusy, onSubmit, onCancel, onAlertBoxClose } = useManageTemplateForm(
		ManageTemplateInstance.User,
		form,
		user,
		null,
		EDIT_USER,
		null,
		returnListUrl,
		_id
	);

	if (loading) return <Loader />;

	const userEmail = user?.email || '';
	const isMarexEmail = userEmail.endsWith('@marex.com') || userEmail.endsWith('@marexspectron.com');

	return (
		<div className={classes.container}>
			<div className={classes.main}>
				<ManageTemplate
					formMessage={formMessage}
					onCancel={onCancel}
					onSubmit={(ev: any): void => {
						onSubmit(ev, { ...form.value, version: data?.document.version });
					}}
					onAlertBoxClose={onAlertBoxClose}
					isNew={false}
					templateInstance={ManageTemplateInstance.User}
					pathArea={'Users'}
					returnUrl={returnListUrl}
					formBusy={formBusy}
					showDelete={false}
					helperContent={
						<div className={classes.userInfo}>
							<LargeUserCircle className={classes.iconCol} />
							<div className={classes.helperCol}>
								<p className={classes.helperColLabel}>Email</p>
								<span className={classes.helperColValue}>{user?.email || 'Anonymous'}</span>
							</div>
							<div className={classes.helperCol}>
								<p className={classes.helperColLabel}>Role</p>
								<span className={classes.helperColValue}>{user?.role}</span>
							</div>
							<div className={classes.helperCol}>
								<p className={classes.helperColLabel}>Last signed in</p>
								<span className={classes.helperColValue}>{moment(user?.lastLogin).format('DD MMM YYYY')}</span>
							</div>
							<div className={classes.helperCol}>
								<p className={classes.helperColLabel}>Member since</p>
								<span className={classes.helperColValue}>{moment(user?.firstLogin).format('DD MMM YYYY')}</span>
							</div>

						</div>
					}>
					<TextField
						id="user-name-input"
						className={classes.disabledTextField}
						disabled
						fullWidth
						label="Name"
						value={user.name} />
					<TextField
						id="user-company-input"
						className={classes.disabledTextField}
						disabled
						fullWidth
						label="Company"
						value={user.company} />
					<TextField
						id="user-telephone-input"
						className={classes.disabledTextField}
						disabled
						fullWidth
						label="Telephone number"
						value={user.phone} />
					<FormTextField
						id="user-function-input"
						label="Function"
						field={form.position}
						placeholder="Research Analyst, Broker, Head of Sales, …"
						charsLimit={50} />
					{
						isMarexEmail && (
							<div className={classes.checkboxWrapper}>
								<Checkbox
									id="show-contributor-photo-checkbox"
									label="Show contributor photo"
									value="showContributorPhoto"
									checked={form.displayAvatar.value}
									onChange={() => form.displayAvatar.setValue(!form.displayAvatar.value)}
								/>
							</div>
						)
					}
					<div className={classes.checkboxWrapper}>
						<Checkbox
							disabled={!isAdmin}
							label="Allow to insert tableau charts into articles"
							value="yes"
							checked={form.tableauAccess.value}
							onChange={() => form.tableauAccess.setValue(!form.tableauAccess.value)}
						/>
					</div>
				</ManageTemplate>
			</div>
		</div>
	);
};

export default UserEdit;
